/**
 * Query Classification Service
 * Analyzes user queries to determine intent and extract entities
 */

export type QueryIntent = 'sales' | 'customers' | 'inventory' | 'payments' | 'orders' | 'general';
export type QueryComplexity = 'simple' | 'medium' | 'complex';

export interface QueryAnalysis {
  intent: QueryIntent;
  complexity: QueryComplexity;
  entities: {
    customers: string[];
    products: string[];
    dates: string[];
    orders: string[];
  };
  keywords: string[];
  originalQuery: string;
}

export class QueryClassifier {
  private intentPatterns: Record<QueryIntent, RegExp[]> = {
    sales: [
      /sales?/i,
      /revenue/i,
      /earnings?/i,
      /income/i,
      /profit/i,
      /today'?s? sales?/i,
      /yesterday'?s? sales?/i,
      /total revenue/i,
      /how much did we make/i,
      /business performance/i
    ],
    customers: [
      /customer/i,
      /client/i,
      /who are.*top customer/i,
      /best customer/i,
      /customer.*spent/i,
      /who.*paid/i,
      /unpaid customer/i,
      /customer.*order/i,
      /loyal customer/i
    ],
    inventory: [
      /stock/i,
      /inventory/i,
      /low stock/i,
      /out of stock/i,
      /restock/i,
      /raw material/i,
      /ingredient/i,
      /supplies?/i,
      /items?.*stock/i
    ],
    payments: [
      /payment/i,
      /paid/i,
      /unpaid/i,
      /bills?/i,
      /outstanding/i,
      /due amount/i,
      /collection/i,
      /who.*paid/i,
      /payment.*status/i,
      /pending payment/i
    ],
    orders: [
      /order/i,
      /orders?/i,
      /today'?s? orders?/i,
      /yesterday'?s? orders?/i,
      /active orders?/i,
      /completed orders?/i,
      /order.*status/i,
      /recent orders?/i
    ],
    general: [
      /help/i,
      /what can you do/i,
      /how are you/i,
      /hello/i,
      /hi/i,
      /business overview/i,
      /summary/i,
      /dashboard/i,
      /report/i
    ]
  };

  private complexityPatterns = {
    simple: [
      /today'?s? sales?/i,
      /show.*top customers?/i,
      /low stock/i,
      /who.*paid/i,
      /unpaid/i,
      /total revenue/i,
      /how many orders?/i,
      /inventory status/i
    ],
    complex: [
      /analyze/i,
      /compare/i,
      /predict/i,
      /forecast/i,
      /trend/i,
      /why/i,
      /how.*work/i,
      /explain/i,
      /recommend/i,
      /suggest/i,
      /optimize/i,
      /improve/i
    ]
  };

  private entityPatterns = {
    customers: [
      /\b[A-Z][a-z]+ [A-Z][a-z]+\b/g, // "John Doe"
      /customer\s+([A-Z][a-z]+)/gi,    // "customer John"
      /([A-Z][a-z]+)\s+spent/gi,      // "John spent"
    ],
    products: [
      /\b(coffee|tea|sandwich|pizza|burger|cake|soup|salad|drink|food)\b/gi,
      /item\s+([A-Za-z]+)/gi,
      /product\s+([A-Za-z]+)/gi
    ],
    dates: [
      /\b(today|yesterday|tomorrow|this week|last week|this month|last month)\b/gi,
      /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/gi,
      /\b\d{1,2}\/\d{1,2}\/\d{4}\b/g,
      /\b\d{4}-\d{2}-\d{2}\b/g
    ],
    orders: [
      /order\s*#?(\w+)/gi,
      /order\s+(\w+)/gi,
      /#(\w+)/g
    ]
  };

  /**
   * Analyze a query and return classification
   */
  analyzeQuery(query: string): QueryAnalysis {
    const normalizedQuery = query.toLowerCase().trim();
    
    return {
      intent: this.classifyIntent(normalizedQuery),
      complexity: this.classifyComplexity(normalizedQuery),
      entities: this.extractEntities(query),
      keywords: this.extractKeywords(normalizedQuery),
      originalQuery: query
    };
  }

  /**
   * Classify query intent
   */
  private classifyIntent(query: string): QueryIntent {
    // Check patterns in order of specificity
    const intentScores: Record<QueryIntent, number> = {
      sales: 0,
      customers: 0,
      inventory: 0,
      payments: 0,
      orders: 0,
      general: 0
    };

    for (const [intent, patterns] of Object.entries(this.intentPatterns)) {
      for (const pattern of patterns) {
        if (pattern.test(query)) {
          intentScores[intent as QueryIntent]++;
        }
      }
    }

    // Find intent with highest score
    let maxScore = 0;
    let detectedIntent: QueryIntent = 'general';

    for (const [intent, score] of Object.entries(intentScores)) {
      if (score > maxScore) {
        maxScore = score;
        detectedIntent = intent as QueryIntent;
      }
    }

    return detectedIntent;
  }

  /**
   * Classify query complexity
   */
  private classifyComplexity(query: string): QueryComplexity {
    // Check for complex patterns first
    for (const pattern of this.complexityPatterns.complex) {
      if (pattern.test(query)) {
        return 'complex';
      }
    }

    // Check for simple patterns
    for (const pattern of this.complexityPatterns.simple) {
      if (pattern.test(query)) {
        return 'simple';
      }
    }

    // Default to medium complexity
    return 'medium';
  }

  /**
   * Extract entities from query
   */
  private extractEntities(query: string): {
    customers: string[];
    products: string[];
    dates: string[];
    orders: string[];
  } {
    const entities = {
      customers: [] as string[],
      products: [] as string[],
      dates: [] as string[],
      orders: [] as string[]
    };

    // Extract customers
    for (const pattern of this.entityPatterns.customers) {
      const matches = query.match(pattern);
      if (matches) {
        entities.customers.push(...matches.map(m => m.trim()));
      }
    }

    // Extract products
    for (const pattern of this.entityPatterns.products) {
      const matches = query.match(pattern);
      if (matches) {
        entities.products.push(...matches.map(m => m.trim().toLowerCase()));
      }
    }

    // Extract dates
    for (const pattern of this.entityPatterns.dates) {
      const matches = query.match(pattern);
      if (matches) {
        entities.dates.push(...matches.map(m => m.trim().toLowerCase()));
      }
    }

    // Extract orders
    for (const pattern of this.entityPatterns.orders) {
      const matches = query.match(pattern);
      if (matches) {
        entities.orders.push(...matches.map(m => m.trim()));
      }
    }

    // Remove duplicates
    entities.customers = Array.from(new Set(entities.customers));
    entities.products = Array.from(new Set(entities.products));
    entities.dates = Array.from(new Set(entities.dates));
    entities.orders = Array.from(new Set(entities.orders));

    return entities;
  }

  /**
   * Extract keywords from query
   */
  private extractKeywords(query: string): string[] {
    // Remove common words and extract meaningful keywords
    const stopWords = new Set([
      'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
      'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
      'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could',
      'should', 'may', 'might', 'can', 'must', 'shall', 'what', 'when',
      'where', 'why', 'how', 'who', 'which', 'this', 'that', 'these', 'those'
    ]);

    return query
      .split(/\s+/)
      .filter(word => word.length > 2 && !stopWords.has(word))
      .slice(0, 10); // Limit to 10 keywords
  }

  /**
   * Check if query is a follow-up to previous query
   */
  isFollowUpQuery(currentQuery: string, previousAnalysis: QueryAnalysis): boolean {
    const followUpPatterns = [
      /^what about/i,
      /^show me/i,
      /^tell me more/i,
      /^give me details/i,
      /^elaborate/i,
      /^explain/i,
      /^how about/i,
      /^and/i,
      /^also/i,
      /^plus/i
    ];

    const normalizedQuery = currentQuery.toLowerCase().trim();
    
    return followUpPatterns.some(pattern => pattern.test(normalizedQuery)) ||
           this.shareEntities(currentQuery, previousAnalysis);
  }

  /**
   * Check if queries share entities (indicating follow-up)
   */
  private shareEntities(currentQuery: string, previousAnalysis: QueryAnalysis): boolean {
    const currentEntities = this.extractEntities(currentQuery);
    
    // Check if they share customers
    const sharedCustomers = currentEntities.customers.some(customer =>
      previousAnalysis.entities.customers.some(prev => 
        prev.toLowerCase().includes(customer.toLowerCase()) ||
        customer.toLowerCase().includes(prev.toLowerCase())
      )
    );

    // Check if they share products
    const sharedProducts = currentEntities.products.some(product =>
      previousAnalysis.entities.products.some(prev => 
        prev.toLowerCase().includes(product.toLowerCase()) ||
        product.toLowerCase().includes(prev.toLowerCase())
      )
    );

    return sharedCustomers || sharedProducts;
  }

  /**
   * Get data requirements for a query intent
   */
  getDataRequirements(intent: QueryIntent): string[] {
    const requirements: Record<QueryIntent, string[]> = {
      sales: ['orders', 'payments', 'menuItems'],
      customers: ['customers', 'orders'],
      inventory: ['rawMaterials', 'inventoryTransactions'],
      payments: ['payments', 'orders'],
      orders: ['orders', 'menuItems'],
      general: ['orders', 'customers', 'payments', 'rawMaterials']
    };

    return requirements[intent] || requirements.general;
  }

  /**
   * Check if query needs real-time data
   */
  needsRealTimeData(intent: QueryIntent): boolean {
    const realTimeIntents: QueryIntent[] = ['sales', 'orders', 'payments'];
    return realTimeIntents.includes(intent);
  }
}

// Singleton instance
export const queryClassifier = new QueryClassifier();
